import { ChangeDetectionStrategy, Component } from '@angular/core';

import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    BaseNestedFieldDefinition,
    FieldType
} from '../../../../../core/field-definitions/entities/field-definition.entity';
import { ExtendedBaseProfileField } from '../../../../../core/profiles/entities/extended-profile-field.entity';
import { PartialRecord } from '../../../../../core/records/entities/partial-record.class';
import { throwIfNullOrUndefined } from '../../../../../helpers/typed-reactive-filters.helper';
import { ComplexFieldValue } from '../../../../entities/field-values/field-type-values.entity';
import { FieldReadHelper } from '../../../../helpers/fields/field-read.helper';
import { ChildFieldToProfileField } from '../../../../helpers/to-profile-field/to-profile-field.helper';
import { ShSearchRoutingService } from '../../../../services/search-routing.service';
import { ShBaseGenericReadonlyFieldComponent } from '../../base-generic-readonly-field/base-generic-readonly-field.component';

@Component({
    selector: 'sh-complex-map-readonly-field',
    templateUrl: './complex-map-readonly-field.component.html',
    styleUrls: ['../../base-generic-readonly-field/base-generic-readonly-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShComplexMapReadonlyFieldComponent extends ShBaseGenericReadonlyFieldComponent {
    public static override readonly IS_COMPLEX: boolean = true;

    public childDefinitions$!: Observable<BaseNestedFieldDefinition[]>;
    public childProfileFields$!: Observable<ExtendedBaseProfileField[]>;

    public value: ComplexFieldValue | ComplexFieldValue[] | null;
    public isList: boolean = false;

    protected type: FieldType = FieldType.ComplexField;

    constructor(
        protected fieldHelper: FieldReadHelper,
        protected searchRoutingService: ShSearchRoutingService
    ) {
        super(searchRoutingService);
        this.initChildDefinitionStreams();
        this.initChildProfileFieldsStream();
    }

    protected initChildDefinitionStreams(): void {
        this.childDefinitions$ = this.profileField$.pipe(
            map(profileField => profileField.FieldDefinition.Children),
            throwIfNullOrUndefined(`Expected ${this.type} to have children`),
            map(children => {
                if (!Array.isArray(children)) {
                    throw new Error(`Expected ${this.type} children to be an array but was ${children}`);
                }
                if (children.length === 0) {
                    throw new Error(`Expected ${this.type} to have at least 1 child, but has ${children.length}`);
                }
                return children;
            })
        );
    }

    protected initChildProfileFieldsStream(): void {
        // Convert child definitions to profile fields and treat like regular fields
        this.childProfileFields$ = combineLatest([this.childDefinitions$, this.profileField$]).pipe(
            map(([children, profileField]) => children.map(child => ChildFieldToProfileField(child, profileField)))
        );
    }

    init(profileField: ExtendedBaseProfileField, record: PartialRecord): void {
        this.value = this.fieldHelper.getComplexFieldValue(profileField.FieldDefinition, record);
    }
}
